import useUpdateTransfer from '@pages/transferDetails/hooks/useUpdateTransfer';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { SnackConfig } from '../types';
import { ActionModalType } from '../types/actionModal.type';

const useActionModal = (type: ActionModalType, onClose: () => void) => {
  const { approveTransfer, rejectTransfer, data, loading, error } =
    useUpdateTransfer();
  const [snackConfig, setSnackConfig] = useState<SnackConfig>({
    message: '',
    severity: 'success',
  });
  const [isSnackOpen, setIsSnackOpen] = useState(false);

  const handleConfirm = async (paymentId: string) => {
    setIsSnackOpen(false);
    setSnackConfig({ message: '', severity: 'success' });

    if (type === 'approve') {
      await approveTransfer(paymentId);
    } else {
      await rejectTransfer(paymentId);
    }
  };

  const resetState = () => {
    setIsSnackOpen(false);
    setSnackConfig({
      message: '',
      severity: 'success',
    });
  };

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose]);

  useEffect(() => {
    resetState();
    return () => {
      resetState();
    };
  }, [type]);

  useEffect(() => {
    if (data && !loading && !error) {
      setSnackConfig({
        message: t(
          type === 'approve'
            ? 'trsfDtlActionConfirmSnack'
            : 'trsfDtlActionRejectSnack'
        ),
        severity: type === 'approve' ? 'success' : 'error',
      });
      setIsSnackOpen(true);

      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  }, [data, loading, error, handleClose, type]);

  useEffect(() => {
    if (error && !loading && !data) {
      setSnackConfig({
        message: t('trsfDtlActionErrorGeneric') || '',
        severity: 'error',
      });
      setIsSnackOpen(true);

      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  }, [error, loading, data, handleClose]);

  return {
    snackConfig,
    isSnackOpen,
    loading,
    error,
    handleConfirm,
    handleClose,
  };
};

export default useActionModal;
